/**
 * PWA Install Prompt Component - BUBBLE VERSION
 * Small, non-intrusive bubble that auto-dismisses
 * Shows once per session only
 */

import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

const PWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBubble, setShowBubble] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if already shown this SESSION (not localStorage)
    const hasShownThisSession = sessionStorage.getItem('pwa-install-shown');

    // Check if already installed/standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        window.navigator.standalone ||
                        document.referrer.includes('android-app://');

    if (isStandalone || hasShownThisSession) {
      return; // Don't show
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Check again (in case sessionStorage was set after initial check)
      const alreadyShown = sessionStorage.getItem('pwa-install-shown');
      if (!alreadyShown) {
        // Show bubble after 3 seconds
        setTimeout(() => {
          setShowBubble(true);
          sessionStorage.setItem('pwa-install-shown', 'true');

          // Fade in
          setTimeout(() => setIsVisible(true), 50);

          // Auto-dismiss after 6 seconds
          setTimeout(() => {
            handleDismiss();
          }, 6000);
        }, 3000);
      }
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setShowBubble(false);
      setIsVisible(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    setDeferredPrompt(null);
    handleDismiss();
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Wait for fade out animation
    setTimeout(() => setShowBubble(false), 300);
  };

  if (!showBubble) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <div className="bg-white rounded-lg shadow-lg border-l-4 border-orange-500 p-4 max-w-xs flex items-start gap-3">
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
          <Download className="w-5 h-5 text-orange-600" />
        </div>

        {/* Content */}
        <div className="flex-1 pr-4">
          <p className="text-sm text-gray-700 mb-2">
            Add to Home Screen for quick access
          </p>

          {deferredPrompt && (
            <button
              onClick={handleInstallClick}
              className="w-full bg-orange-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors"
            >
              Install App
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Mini install button for header/navigation (unchanged)
export const PWAInstallButton = () => {
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setCanInstall(false);
    }

    setDeferredPrompt(null);
  };

  if (!canInstall) return null;

  return (
    <button
      onClick={handleInstall}
      className="hidden md:flex items-center px-3 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
      title="Install App"
    >
      <Download className="w-4 h-4 mr-1" />
      Install
    </button>
  );
};

export default PWAInstall;
